import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Bell,
  Clock,
  LogOut,
  Maximize2,
  Menu as MenuIcono,
  Minimize2,
  Shield,
  User,
} from 'lucide-react';
import { api } from '@/shared/api/cliente';
import type { Menu } from '@/shared/api/tipos';
import { useSesion } from '@/shared/sesion/SesionProvider';
import { Icono } from '@/shared/ui/Icono';

interface Props {
  alAbrirMenu: () => void;
}

export function BarraSuperior({ alAbrirMenu }: Props) {
  const { perfil, salir } = useSesion();
  const ubicacion = useLocation();

  const [desplegado, setDesplegado] = useState(false);
  const [avisosAbiertos, setAvisosAbiertos] = useState(false);
  const [pantallaCompleta, setPantallaCompleta] = useState(false);

  const refDesplegable = useRef<HTMLDivElement>(null);
  const refAvisos = useRef<HTMLDivElement>(null);

  const { data: menu } = useQuery({
    queryKey: ['menu'],
    queryFn: () => api.get<Menu>('/menu'),
    staleTime: 5 * 60_000,
  });

  const actual = entradaDe(menu, ubicacion.pathname);

  useEffect(() => {
    function alPulsarFuera(evento: MouseEvent) {
      const destino = evento.target as Node;
      if (refDesplegable.current && !refDesplegable.current.contains(destino)) {
        setDesplegado(false);
      }
      if (refAvisos.current && !refAvisos.current.contains(destino)) {
        setAvisosAbiertos(false);
      }
    }
    document.addEventListener('mousedown', alPulsarFuera);
    return () => document.removeEventListener('mousedown', alPulsarFuera);
  }, []);

  useEffect(() => {
    function alCambiar() {
      setPantallaCompleta(Boolean(document.fullscreenElement));
    }
    document.addEventListener('fullscreenchange', alCambiar);
    return () => document.removeEventListener('fullscreenchange', alCambiar);
  }, []);

  async function alternarPantallaCompleta() {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await document.documentElement.requestFullscreen();
      }
    } catch {
    }
  }

  return (
    <header className="sticky top-0 z-30 flex h-14 shrink-0 items-center justify-between border-b border-armazon-200 bg-white px-4">
      <div className="flex min-w-0 items-center gap-1">

        <button
          type="button"
          onClick={alAbrirMenu}
          aria-label="Abrir el menú"
          className="grid size-9 place-items-center rounded-lg text-armazon-500 transition-colors hover:bg-armazon-100 hover:text-armazon-700 lg:hidden"
        >
          <MenuIcono className="size-5" aria-hidden />
        </button>

        <img
          src="/marca-epagal.webp"
          alt="EPAGAL"
          width={121}
          height={32}
          className="hidden h-8 w-auto px-3 sm:block"
        />

        <span className="mx-1 hidden text-armazon-300 sm:block" aria-hidden>
          /
        </span>

        {actual && (
          <div className="flex min-w-0 items-center gap-1.5 text-sm text-armazon-500">
            <Icono nombre={actual.icono} className="size-3.5 shrink-0 text-armazon-400" />
            <span className="truncate font-medium uppercase tracking-wide">{actual.etiqueta}</span>
          </div>
        )}
      </div>

      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => void alternarPantallaCompleta()}
          title={pantallaCompleta ? 'Salir de pantalla completa' : 'Pantalla completa'}
          aria-label={pantallaCompleta ? 'Salir de pantalla completa' : 'Pantalla completa'}
          className="hidden size-9 place-items-center rounded-lg text-armazon-400 transition-colors hover:bg-armazon-100 hover:text-armazon-600 md:grid"
        >
          {pantallaCompleta ? (
            <Minimize2 className="size-4" aria-hidden />
          ) : (
            <Maximize2 className="size-4" aria-hidden />
          )}
        </button>

        <div ref={refAvisos} className="relative">
          <button
            type="button"
            onClick={() => setAvisosAbiertos((v) => !v)}
            aria-label="Novedades"
            aria-expanded={avisosAbiertos}
            className="grid size-9 place-items-center rounded-lg text-armazon-400 transition-colors hover:bg-armazon-100 hover:text-armazon-600"
          >
            <Bell className="size-[17px]" aria-hidden />
          </button>

          {avisosAbiertos && (
            <div className="absolute right-0 z-50 mt-2 w-72 overflow-hidden rounded-xl border border-armazon-200 bg-white shadow-xl">
              <div className="border-b border-armazon-100 px-4 py-3">
                <p className="text-sm font-semibold text-armazon-800">Novedades</p>
              </div>

              <p className="px-4 py-6 text-center text-xs text-armazon-500">
                No hay novedades. Los avisos de cartera, cobros y SRI se activan cuando
                existan esos módulos.
              </p>
            </div>
          )}
        </div>

        <div ref={refDesplegable} className="relative">
          <button
            type="button"
            onClick={() => setDesplegado((v) => !v)}
            aria-expanded={desplegado}
            className="flex items-center gap-2 rounded-lg py-1.5 pl-1.5 pr-2 transition-colors hover:bg-armazon-100"
          >
            <span className="grid size-8 shrink-0 place-items-center rounded-full bg-marca-500 text-sm font-bold text-white">
              {perfil?.nombre?.trim().charAt(0).toUpperCase() ?? 'U'}
            </span>
            <span className="hidden max-w-[9rem] truncate text-sm text-armazon-700 sm:block">
              {perfil?.nombre}
            </span>
          </button>

          {desplegado && (
            <div className="absolute right-0 z-50 mt-2 w-72 overflow-hidden rounded-xl border border-armazon-200 bg-white shadow-xl">
              <div className="border-b border-armazon-100 bg-gradient-to-r from-marca-50 to-marca-100/60 px-4 py-4">
                <div className="flex items-center gap-3">
                  <span className="grid size-11 shrink-0 place-items-center rounded-full bg-marca-500 text-base font-bold text-white">
                    {perfil?.nombre?.trim().charAt(0).toUpperCase() ?? 'U'}
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold uppercase text-armazon-800">
                      {perfil?.nombre}
                    </p>
                    <p className="truncate text-xs uppercase text-armazon-500">{perfil?.correo}</p>
                  </div>
                </div>
              </div>

              <dl className="space-y-2.5 border-b border-armazon-100 px-4 py-3">
                <Dato icono={<Shield className="size-3" aria-hidden />} etiqueta="Cargo">
                  <span
                    className={[
                      'rounded-full px-2 py-0.5 text-xs font-semibold',
                      perfil?.administrador
                        ? 'bg-amber-50 text-amber-700'
                        : 'bg-marca-50 text-marca-700',
                    ].join(' ')}
                  >
                    {perfil?.cargo ?? perfil?.rol?.toUpperCase() ?? 'Sin cargo'}
                  </span>
                </Dato>

                {perfil?.ultimoAccesoEn && (
                  <Dato icono={<Clock className="size-3" aria-hidden />} etiqueta="Última conexión">
                    <span className="text-xs text-armazon-600">
                      {new Date(perfil.ultimoAccesoEn).toLocaleString('es-EC', {
                        day: '2-digit',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </Dato>
                )}

                <Dato icono={<User className="size-3" aria-hidden />} etiqueta="Estado">
                  <span
                    className={[
                      'inline-flex items-center gap-1 text-xs font-medium',
                      perfil?.activo === false ? 'text-alerta' : 'text-marca-600',
                    ].join(' ')}
                  >
                    <span
                      className={[
                        'size-1.5 rounded-full',
                        perfil?.activo === false ? 'bg-alerta' : 'bg-marca-500',
                      ].join(' ')}
                      aria-hidden
                    />
                    {perfil?.activo === false ? 'Inactivo' : 'Activo'}
                  </span>
                </Dato>
              </dl>

              <button
                type="button"
                onClick={() => {
                  setDesplegado(false);
                  void salir();
                }}
                className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm text-armazon-600 transition-colors hover:bg-red-50 hover:text-alerta"
              >
                <LogOut className="size-[15px]" aria-hidden />
                Cerrar sesión
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

function Dato({
  icono,
  etiqueta,
  children,
}: {
  icono: React.ReactNode;
  etiqueta: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <dt className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-armazon-400">
        {icono}
        {etiqueta}
      </dt>
      <dd className="min-w-0 truncate">{children}</dd>
    </div>
  );
}

function entradaDe(menu: Menu | undefined, ruta: string) {
  if (!menu) {
    return null;
  }
  const todas = [...menu.superiores, ...menu.inferiores, ...menu.grupos.flatMap((g) => g.items)];

  return (
    todas
      .filter((i) => ruta === i.href || ruta.startsWith(`${i.href}/`))
      .sort((a, b) => b.href.length - a.href.length)[0] ?? null
  );
}
